import { useState } from "react";
import { Button } from "../../base/Button";
import { HtmlPreviewCodeBlock } from "../../base/RunnableCodeBlock";
import { Play, RotateCcw } from "lucide-react";

const DEFAULT_HTML = `<h1>こんにちは！</h1>
<p>私の名前は○○です。</p>
<p>好きな食べ物は<strong>カレー</strong>です。</p>`;

// Day1: HTMLを書いてブラウザに表示する体験
function Day1Experience() {
  return (
    <div className="my-8 space-y-4">
      <HtmlPreviewCodeBlock
        initialCode={DEFAULT_HTML}
      />
    </div>
  );
}

// Day2: ブラウザとサーバーの会話体験（天気予報で説明）
function Day2Experience() {
  const [step, setStep] = useState<"ready" | "sending" | "received">("ready");
  const [weather, setWeather] = useState<any>(null);

  const handleSend = async () => {
    setStep("sending");

    // アニメーション効果のため少し待つ
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 擬似的な天気データ（実際のAPIを使わず、分かりやすいデータを表示）
    setWeather({
      city: "東京",
      temp: 18,
      condition: "晴れ",
      humidity: 45,
    });
    setStep("received");
  };

  const handleReset = () => {
    setStep("ready");
    setWeather(null);
  };

  return (
    <div className="my-8">
      <div className="bg-second rounded-2xl p-6">
        <div className="space-y-4">
          {/* ブラウザ（あなた） */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl shrink-0">
              💻
            </div>
            <div className="flex-1">
              <div className="max-sm:text-[0.9375rem] text-xs text-sub mb-1">あなたのブラウザ</div>
              <div className={`bg-blue-50 rounded-2xl rounded-tl-none p-4 ${step === "sending" ? "animate-pulse" : ""}`}>
                <p className="max-sm:text-[0.9375rem] text-sm text-blue-800 font-bold">
                  「東京の天気を教えて！」
                </p>
              </div>
            </div>
          </div>

          {/* 矢印 */}
          {step !== "ready" && (
            <div className="flex justify-center py-2">
              <div className={`flex flex-col items-center gap-1 ${step === "sending" ? "animate-pulse" : ""}`}>
                <span className="text-2xl">{step === "sending" ? "⬇️" : "↕️"}</span>
                <span className="max-sm:text-[0.9375rem] text-xs text-sub">
                  {step === "sending" ? "問い合わせ中..." : "通信完了"}
                </span>
              </div>
            </div>
          )}

          {/* サーバー */}
          {step !== "ready" && (
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-2xl shrink-0">
                🌐
              </div>
              <div className="flex-1">
                <div className="max-sm:text-[0.9375rem] text-xs text-sub mb-1">天気サーバー</div>
                <div className={`bg-green-50 rounded-2xl rounded-tl-none p-4 ${step === "sending" ? "opacity-50" : ""}`}>
                  {step === "sending" ? (
                    <p className="max-sm:text-[0.9375rem] text-sm text-green-800">データを探しています...</p>
                  ) : (
                    <>
                      <p className="max-sm:text-[0.9375rem] text-sm text-green-800 font-bold">「はい、どうぞ！」</p>
                      {weather && (
                        <div className="mt-3 p-4 bg-white rounded-xl">
                          <div className="flex items-center gap-3">
                            <span className="text-4xl">☀️</span>
                            <div>
                              <p className="text-lg font-bold text-gray-800">{weather.city}の天気</p>
                              <p className="text-2xl font-bold text-blue-600">{weather.temp}°C</p>
                              <p className="max-sm:text-[0.9375rem] text-sm text-gray-600">{weather.condition}・湿度 {weather.humidity}%</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ボタン */}
        <div className="mt-6 flex justify-center">
          {step === "ready" ? (
            <Button onClick={handleSend} variant="primary" size="lg">
              <Play className="w-4 h-4 mr-2" />
              天気を聞いてみる
            </Button>
          ) : step === "received" ? (
            <Button onClick={handleReset} variant="outline" size="sm">
              <RotateCcw className="w-4 h-4 mr-2" />
              もう一度
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Day3: プログラムを動かす体験（コードは見せない、結果だけ）
function Day3Experience() {
  const [step, setStep] = useState<"ready" | "running" | "done">("ready");
  const [result, setResult] = useState<any>(null);

  const handleRun = async () => {
    setStep("running");

    // 処理中の演出
    await new Promise(resolve => setTimeout(resolve, 500));

    // 擬似的な処理結果
    const scores = [85, 92, 78, 95, 88];
    const total = scores.reduce((a, b) => a + b, 0);
    const average = Math.round(total / scores.length);
    const max = Math.max(...scores);
    const min = Math.min(...scores);

    setResult({ scores, total, average, max, min });
    setStep("done");
  };

  const handleReset = () => {
    setStep("ready");
    setResult(null);
  };

  return (
    <div className="my-8">
      <div className="bg-second rounded-2xl p-6">
        {/* 入力データ */}
        <div className="mb-4">
          <p className="max-sm:text-[0.9375rem] text-sm text-sub mb-2">5人のテストの点数：</p>
          <div className="flex gap-2 flex-wrap">
            {[85, 92, 78, 95, 88].map((score, i) => (
              <div key={i} className="bg-white px-4 py-2 rounded-lg text-center">
                <div className="max-sm:text-[0.9375rem] text-xs text-sub">{i + 1}人目</div>
                <div className="text-lg font-bold text-primary">{score}点</div>
              </div>
            ))}
          </div>
        </div>

        {/* 処理中・結果表示 */}
        {step === "running" && (
          <div className="my-6 p-4 bg-yellow-50 rounded-xl text-center">
            <div className="text-2xl animate-spin inline-block">⚙️</div>
            <p className="max-sm:text-[0.9375rem] text-sm text-yellow-800 mt-2">計算中...</p>
          </div>
        )}

        {step === "done" && result && (
          <div className="my-6 p-4 bg-green-50 rounded-xl">
            <p className="max-sm:text-[0.9375rem] text-sm text-green-800 font-bold mb-3">📊 計算結果</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white p-3 rounded-lg text-center">
                <div className="max-sm:text-[0.9375rem] text-xs text-sub">合計</div>
                <div className="text-xl font-bold text-gray-800">{result.total}点</div>
              </div>
              <div className="bg-white p-3 rounded-lg text-center">
                <div className="max-sm:text-[0.9375rem] text-xs text-sub">平均</div>
                <div className="text-xl font-bold text-blue-600">{result.average}点</div>
              </div>
              <div className="bg-white p-3 rounded-lg text-center">
                <div className="max-sm:text-[0.9375rem] text-xs text-sub">最高点</div>
                <div className="text-xl font-bold text-green-600">{result.max}点</div>
              </div>
              <div className="bg-white p-3 rounded-lg text-center">
                <div className="max-sm:text-[0.9375rem] text-xs text-sub">最低点</div>
                <div className="text-xl font-bold text-orange-600">{result.min}点</div>
              </div>
            </div>
            <p className="max-sm:text-[0.9375rem] text-xs text-green-700 mt-3 text-center">
              ✓ プログラムがこれを一瞬で計算しました
            </p>
          </div>
        )}

        {/* ボタン */}
        <div className="flex justify-center">
          {step === "ready" ? (
            <Button onClick={handleRun} variant="primary" size="lg">
              <Play className="w-4 h-4 mr-2" />
              計算する
            </Button>
          ) : step === "done" ? (
            <Button onClick={handleReset} variant="outline" size="sm">
              <RotateCcw className="w-4 h-4 mr-2" />
              もう一度
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Day4: プロの判断を体験（選択式）
function Day4Experience() {
  const [selected, setSelected] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);

  const handleSelect = (choice: string) => {
    setSelected(choice);
  };

  const handleSubmit = () => {
    setShowResult(true);
  };

  const handleReset = () => {
    setSelected(null);
    setShowResult(false);
  };

  return (
    <div className="my-8">
      <div className="bg-second rounded-2xl p-6">
        {/* シナリオ */}
        <div className="mb-6">
          <p className="max-sm:text-[0.9375rem] text-sm text-sub mb-2">📊 あなたのWebサイトのデータ：</p>
          <div className="bg-white rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="max-sm:text-[0.9375rem] text-sm text-gray-600">ページの表示速度</span>
              <span className="max-sm:text-[0.9375rem] text-sm font-bold text-red-500">5.2秒（遅い）</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="max-sm:text-[0.9375rem] text-sm text-gray-600">すぐ帰る人の割合</span>
              <span className="max-sm:text-[0.9375rem] text-sm font-bold text-red-500">40%（高い）</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="max-sm:text-[0.9375rem] text-sm text-gray-600">スマホユーザー</span>
              <span className="max-sm:text-[0.9375rem] text-sm font-bold text-blue-500">70%</span>
            </div>
          </div>
        </div>

        {!showResult ? (
          <>
            {/* 質問 */}
            <p className="max-sm:text-[0.9375rem] text-sm font-bold text-main mb-4">
              このデータを見て、最初に何を改善しますか？
            </p>

            {/* 選択肢 */}
            <div className="space-y-3 mb-6">
              {[
                { id: "speed", label: "ページの表示速度を上げる", icon: "⚡" },
                { id: "design", label: "デザインをかっこよくする", icon: "🎨" },
                { id: "content", label: "記事をもっと増やす", icon: "📝" },
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    selected === option.id
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span className="text-xl mr-2">{option.icon}</span>
                  <span className="max-sm:text-[0.9375rem] text-sm font-medium">{option.label}</span>
                </button>
              ))}
            </div>

            {/* 決定ボタン */}
            <div className="flex justify-center">
              <Button
                onClick={handleSubmit}
                variant="primary"
                size="lg"
                disabled={!selected}
              >
                この答えで決定
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* 結果 */}
            <div className={`p-4 rounded-xl mb-4 ${
              selected === "speed" ? "bg-green-50" : "bg-yellow-50"
            }`}>
              {selected === "speed" ? (
                <>
                  <p className="text-green-800 font-bold mb-2">✅ 正解！プロと同じ判断です</p>
                  <p className="max-sm:text-[0.9375rem] text-sm text-green-700">
                    ページが遅いと、ユーザーは待てずに帰ってしまいます。<br />
                    特にスマホユーザーが70%もいるので、速度改善が最優先です。
                  </p>
                </>
              ) : (
                <>
                  <p className="text-yellow-800 font-bold mb-2">🤔 惜しい！</p>
                  <p className="max-sm:text-[0.9375rem] text-sm text-yellow-700">
                    プロは「ページの表示速度」を選びます。<br />
                    なぜなら、どんなに良いデザインや記事があっても、<br />
                    表示が遅いとユーザーは見る前に帰ってしまうからです。
                  </p>
                </>
              )}
            </div>

            <div className="flex justify-center">
              <Button onClick={handleReset} variant="outline" size="sm">
                <RotateCcw className="w-4 h-4 mr-2" />
                もう一度
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Day5: Webアプリ体験（シンプルなダッシュボード）
function Day5Experience() {
  const [isRunning, setIsRunning] = useState(false);
  const [stats, setStats] = useState({
    users: 1234,
    sales: 56789,
    views: 9876,
  });

  const handleRun = () => {
    setIsRunning(true);

    // データが更新される演出
    const interval = setInterval(() => {
      setStats(prev => ({
        users: prev.users + Math.floor(Math.random() * 10),
        sales: prev.sales + Math.floor(Math.random() * 1000),
        views: prev.views + Math.floor(Math.random() * 50),
      }));
    }, 500);

    // 3秒後に停止
    setTimeout(() => {
      clearInterval(interval);
      setIsRunning(false);
    }, 3000);
  };

  return (
    <div className="my-8">
      <div className="bg-second rounded-2xl p-6">
        {/* ダッシュボード */}
        <div className="bg-white rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800">📊 マイダッシュボード</h3>
            {isRunning && (
              <span className="max-sm:text-[0.9375rem] text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full animate-pulse">
                ● リアルタイム更新中
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-1">👥</div>
              <div className={`text-xl font-bold text-blue-600 ${isRunning ? "animate-pulse" : ""}`}>
                {stats.users.toLocaleString()}
              </div>
              <div className="max-sm:text-[0.9375rem] text-xs text-gray-500">ユーザー数</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-1">💰</div>
              <div className={`text-xl font-bold text-green-600 ${isRunning ? "animate-pulse" : ""}`}>
                ¥{stats.sales.toLocaleString()}
              </div>
              <div className="max-sm:text-[0.9375rem] text-xs text-gray-500">売上</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-1">👀</div>
              <div className={`text-xl font-bold text-purple-600 ${isRunning ? "animate-pulse" : ""}`}>
                {stats.views.toLocaleString()}
              </div>
              <div className="max-sm:text-[0.9375rem] text-xs text-gray-500">ページビュー</div>
            </div>
          </div>
        </div>

        {/* 説明 */}
        <p className="max-sm:text-[0.9375rem] text-xs text-sub text-center mb-4">
          ↑ これがWebアプリです。ブラウザ・サーバー・データが連携して動いています。
        </p>

        {/* ボタン */}
        <div className="flex justify-center">
          <Button
            onClick={handleRun}
            variant="primary"
            size="lg"
            disabled={isRunning}
          >
            {isRunning ? (
              <>
                <span className="animate-spin mr-2">⚙️</span>
                動作中...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                アプリを動かす
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// メインコンポーネント
export interface ExperienceBlockProps {
  type: "day1" | "day2" | "day3" | "day4" | "day5";
}

export function ExperienceBlock({ type }: ExperienceBlockProps) {
  switch (type) {
    case "day1":
      return <Day1Experience />;
    case "day2":
      return <Day2Experience />;
    case "day3":
      return <Day3Experience />;
    case "day4":
      return <Day4Experience />;
    case "day5":
      return <Day5Experience />;
    default:
      return null;
  }
}
